<template>
  <div class="m-feedback-form">
    <aSection
      :background="'#fff'"
      :title="'feedback_form_title'"
      :subtitle="'feedback_form_subtitle'"
    >
      <div class="container feedback-form__container">
        <div class="feedback-form__intro">
          <h3 class="feedback-form__intro-title">
            {{ $t("feedback_intro_title") }}
          </h3>
          <p class="feedback-form__intro-text">
            {{ $t("feedback_intro_text") }}
          </p>
          <div class="feedback-form__intro-contact">
            <a
              href="mailto:office@luxbrumalis.ro"
              class="feedback-form__intro-contact-item"
            >
              <span class="feedback-form__intro-contact-icon">
                <SfIcon :icon="'mail'" />
              </span>
              <span>office@luxbrumalis.ro</span>
            </a>
          </div>
        </div>

        <form
          class="feedback-form__form"
          @submit.prevent="submitForm"
          novalidate
        >
          <div class="feedback-form__row">
            <div class="feedback-form__field">
              <label class="feedback-form__label">
                {{ $t("feedback_name") }} <span>*</span>
              </label>
              <input
                class="feedback-form__input"
                :class="{ 'feedback-form__input--error': errors.name }"
                type="text"
                v-model="form.name"
                :placeholder="$t('feedback_name_ph')"
              />
              <span v-if="errors.name" class="feedback-form__error-msg">
                {{ $t(errors.name) }}
              </span>
            </div>
            <div class="feedback-form__field">
              <label class="feedback-form__label">
                {{ $t("feedback_email") }} <span>*</span>
              </label>
              <input
                class="feedback-form__input"
                :class="{ 'feedback-form__input--error': errors.email }"
                type="email"
                v-model="form.email"
                :placeholder="$t('feedback_email_ph')"
              />
              <span v-if="errors.email" class="feedback-form__error-msg">
                {{ $t(errors.email) }}
              </span>
            </div>
          </div>

          <div class="feedback-form__row">
            <div class="feedback-form__field">
              <label class="feedback-form__label">
                {{ $t("feedback_phone") }}
              </label>
              <input
                class="feedback-form__input"
                type="tel"
                v-model="form.phone"
                :placeholder="$t('feedback_phone_ph')"
                @input="filterPhone"
              />
            </div>
            <div class="feedback-form__field">
              <label class="feedback-form__label">
                {{ $t("feedback_type") }} <span>*</span>
              </label>
              <select
                class="feedback-form__input feedback-form__select"
                :class="{ 'feedback-form__input--error': errors.type }"
                v-model="form.type"
              >
                <option value="" disabled>
                  {{ $t("feedback_type_select") }}
                </option>
                <option
                  v-for="option in feedbackTypes"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ $t(option.label) }}
                </option>
              </select>
              <span v-if="errors.type" class="feedback-form__error-msg">
                {{ $t(errors.type) }}
              </span>
            </div>
          </div>

          <div class="feedback-form__field feedback-form__field--full">
            <label class="feedback-form__label">
              {{ $t("feedback_order_number") }}
            </label>
            <input
              class="feedback-form__input"
              type="text"
              v-model="form.orderNumber"
              :placeholder="$t('feedback_order_number_ph')"
            />
          </div>

          <div class="feedback-form__field feedback-form__field--full">
            <label class="feedback-form__label">
              {{ $t("feedback_message") }} <span>*</span>
            </label>
            <textarea
              class="feedback-form__input feedback-form__textarea"
              :class="{ 'feedback-form__input--error': errors.message }"
              v-model="form.message"
              :placeholder="$t('feedback_message_ph')"
              rows="5"
            ></textarea>
            <span v-if="errors.message" class="feedback-form__error-msg">
              {{ $t(errors.message) }}
            </span>
          </div>

          <div class="feedback-form__field feedback-form__field--full">
            <label class="feedback-form__checkbox-wrapper">
              <input
                type="checkbox"
                class="feedback-form__checkbox"
                :class="{ 'feedback-form__checkbox--error': errors.consent }"
                v-model="form.consent"
              />
              <span class="feedback-form__checkbox-text">
                {{ $t("feedback_consent_text") }}
                <a :href="privacyPath" target="_blank">
                  {{ $t("Politica de Confidențialitate") }}
                </a>
                {{ $t("feedback_consent_and") }}
                <a :href="termsPath" target="_blank">
                  {{ $t("Termeni și Condiții") }} </a
                >.
              </span>
            </label>
            <span v-if="errors.consent" class="feedback-form__error-msg">
              {{ $t(errors.consent) }}
            </span>
          </div>

          <button
            type="submit"
            class="feedback-form__submit"
            :class="{ 'feedback-form__submit--loading': isLoading }"
            :disabled="isLoading"
          >
            <span>{{
              isLoading ? $t("feedback_submitting") : $t("feedback_submit")
            }}</span>
          </button>
        </form>
      </div>
    </aSection>
  </div>
</template>

<script>
import aSection from "../atoms/a-section.vue";
import { SfIcon } from "@storefront-ui/vue";
import axios from "axios";

export default {
  components: {
    aSection,
    SfIcon,
  },
  computed: {
    privacyPath() {
      return `/${this.$root.$i18n.locale}/politica-de-confidentialitate`;
    },
    termsPath() {
      return `/${this.$root.$i18n.locale}/termeni-si-conditii`;
    },
  },
  data() {
    return {
      isLoading: false,
      feedbackTypes: [
        { value: "complaint", label: "feedback_type_complaint" },
        { value: "suggestion", label: "feedback_type_suggestion" },
        { value: "question", label: "feedback_type_question" },
        { value: "other", label: "feedback_type_other" },
      ],
      form: {
        name: "",
        email: "",
        phone: "",
        type: "",
        orderNumber: "",
        message: "",
        consent: false,
      },
      errors: {
        name: "",
        email: "",
        type: "",
        message: "",
        consent: "",
      },
    };
  },
  methods: {
    validate() {
      let valid = true;
      this.errors = { name: "", email: "", type: "", message: "", consent: "" };

      if (!this.form.name.trim()) {
        this.errors.name = "feedback_field_required";
        valid = false;
      }
      if (!this.form.email.trim()) {
        this.errors.email = "feedback_field_required";
        valid = false;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email)) {
        this.errors.email = "feedback_email_invalid";
        valid = false;
      }
      if (!this.form.type) {
        this.errors.type = "feedback_field_required";
        valid = false;
      }
      if (!this.form.message.trim()) {
        this.errors.message = "feedback_field_required";
        valid = false;
      }
      if (!this.form.consent) {
        this.errors.consent = "feedback_consent_required";
        valid = false;
      }
      return valid;
    },
    async submitForm() {
      if (!this.validate()) return;

      this.isLoading = true;
      try {
        const { consent, ...payload } = this.form;
        await axios.post("https://jeffbrasov.ro/api/contact", payload);
        this.$store.dispatch("spawnNotification", {
          type: "success",
          message: this.$t("feedback_success_message"),
          action1: { label: "OK" },
        });
        this.resetForm();
      } catch {
        this.$store.dispatch("spawnNotification", {
          type: "danger",
          message: this.$t("feedback_error_message"),
          action1: { label: "OK" },
        });
      } finally {
        this.isLoading = false;
      }
    },
    resetForm() {
      this.form = {
        name: "",
        email: "",
        phone: "",
        type: "",
        orderNumber: "",
        message: "",
        consent: false,
      };
    },
    filterPhone() {
      this.form.phone = this.form.phone.replace(/[^0-9+\s().-]/g, "");
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@storefront-ui/shared/styles/helpers/breakpoints";

.m-feedback-form {
  width: 100%;
}

.feedback-form {
  &__container {
    display: flex;
    gap: var(--spacer-2xl);
    align-items: flex-start;
    @include for-mobile {
      flex-direction: column;
      gap: var(--spacer-lg);
      padding: 0 var(--spacer-sm);
      width: calc(100% - 2rem);
    }
  }

  &__intro {
    flex: 0 0 30%;
    @include for-mobile {
      flex: unset;
      width: 100%;
    }

    &-title {
      font-size: 1.75rem;
      font-weight: var(--font-semibold);
      color: var(--c-dark);
      margin: 0 0 var(--spacer-base);
    }

    &-text {
      font-size: var(--font-base);
      line-height: 1.7;
      color: var(--c-dark-lighten);
      margin: 0 0 var(--spacer-xl);
    }

    &-contact {
      display: flex;
      flex-direction: column;
      gap: var(--spacer-sm);
    }

    &-contact-item {
      display: flex;
      align-items: center;
      gap: var(--spacer-xs);
      text-decoration: none;
      color: var(--c-dark);
      font-size: var(--font-sm);
      font-weight: var(--font-medium);
      transition: 0.2s ease;

      &:hover {
        color: var(--c-primary-darken);
      }
    }

    &-contact-icon {
      --icon-size: 1.125rem;
      --icon-color: var(--c-white);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: var(--spacer-2xs);
      background-color: var(--c-primary-darken);
      border-radius: 6px;
      flex-shrink: 0;
    }
  }

  &__form {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: var(--spacer-base);
    @include for-mobile {
      width: 100%;
    }
  }

  &__row {
    display: flex;
    gap: var(--spacer-base);
    @include for-mobile {
      flex-direction: column;
      gap: var(--spacer-sm);
    }
  }

  &__field {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: var(--spacer-2xs);

    &--full {
      width: 100%;
    }
  }

  &__label {
    font-size: var(--font-sm);
    font-weight: var(--font-semibold);
    color: var(--c-dark);

    span {
      color: var(--c-primary-darken);
    }
  }

  &__input {
    padding: 0.75rem var(--spacer-sm);
    border: 1.5px solid #d8e8f0;
    border-radius: 8px;
    font-size: var(--font-base);
    font-family: var(--font-family-primary);
    color: var(--c-dark);
    background-color: #f7fbfd;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    width: 100%;
    box-sizing: border-box;
    outline: none;
    -webkit-appearance: none;
    appearance: none;

    &::placeholder {
      color: #a0bbc9;
    }

    &:focus {
      border-color: var(--c-primary-darken);
      box-shadow: 0 0 0 3px rgba(65, 182, 230, 0.15);
      background-color: #fff;
    }

    &--error {
      border-color: #e53e3e;
      background-color: #fff5f5;

      &:focus {
        box-shadow: 0 0 0 3px rgba(229, 62, 62, 0.1);
      }
    }
  }

  &__select {
    cursor: pointer;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2341b6e6' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right var(--spacer-sm) center;
    padding-right: 2.5rem;
  }

  &__textarea {
    resize: vertical;
    min-height: 7rem;
    line-height: 1.6;
  }

  &__error-msg {
    font-size: var(--font-xs);
    color: #e53e3e;
    font-weight: var(--font-medium);
  }

  &__submit {
    align-self: flex-start;
    padding: 0.875rem var(--spacer-2xl);
    background-color: var(--c-primary-darken);
    color: var(--c-white);
    font-size: var(--font-base);
    font-family: var(--font-family-primary);
    font-weight: var(--font-semibold);
    border: none;
    border-radius: 200px;
    cursor: pointer;
    transition: 0.3s ease;
    filter: drop-shadow(1px 2px 6px rgba(31, 54, 90, 0.2));
    @include for-mobile {
      align-self: stretch;
      text-align: center;
    }

    &:hover:not(:disabled) {
      background-color: var(--c-primary);
      transform: translateY(-1px);
    }

    &:disabled,
    &--loading {
      opacity: 0.7;
      cursor: not-allowed;
    }
  }

  &__checkbox-wrapper {
    display: flex;
    align-items: center;
    gap: var(--spacer-xs);
    cursor: pointer;
  }

  &__checkbox {
    margin-top: 0.25rem;
    width: 1.125rem;
    height: 1.125rem;
    cursor: pointer;
    flex-shrink: 0;
    -webkit-appearance: none;
    appearance: none;
    border: 1.5px solid #d8e8f0;
    border-radius: 4px;
    background-color: #f7fbfd;
    transition: background-color 0.2s ease, border-color 0.2s ease;

    &:checked {
      background-color: var(--c-primary-darken);
      border-color: var(--c-primary-darken);
    }

    &--error {
      outline: 2px solid #e53e3e;
      outline-offset: 1px;
    }
  }

  &__checkbox-text {
    font-size: var(--font-sm);
    line-height: 1.5;
    color: var(--c-dark-lighten);

    a {
      color: var(--c-primary-darken);
      text-decoration: none;
      font-weight: var(--font-medium);

      &:hover {
        text-decoration: underline;
      }
    }
  }
}
</style>
